import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { StyledLabel } from 'src/components/AddClientModal/dynamic-property-element';
import { AvatarUploader } from 'src/components/CreateUserModal/avatar-uploader';
import {
  AvatarFlushFailureReason,
  AvatarFlushStatus,
  useAvatarSelection,
} from 'src/components/CreateUserModal/use-avatar-selection';
import { UserModalActions } from 'src/components/CreateUserModal/user-modal-actions';
import {
  EMAIL_VALIDATION_REGEX,
  MAX_NUMBER_LENGTH,
  MIN_NUMBER_LENGTH,
} from 'src/components/Forms';
import { PHONE_FIELD_PLACEHOLDER } from 'src/components/Forms/const';
import { TextMaskPhone } from 'src/components/Forms/FormPhoneInput/form-phone-input';
import { appendCodeToPhoneNumber } from 'src/components/Forms/utils';
import { getPlatformUserAvatarStorageKey } from 'src/graphql/entities/platform-user/platform-user-avatar-url';
import { useDeletePlatformUserAvatar } from 'src/graphql/entities/platform-user/use-delete-platform-user-avatar';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { CreateUserFormFields, CreateUserFormModel } from './types';

export const CreateUserModal = () => {
  const { closeModal } = useModalMethods(Modals.CreateUser);
  const { isOpened, modalProps } = useModalState(Modals.CreateUser);

  const formMethods = useForm<CreateUserFormModel>({
    mode: 'all',
    values: {
      [CreateUserFormFields.FirstName]: '',
      [CreateUserFormFields.LastName]: '',
      [CreateUserFormFields.Email]: '',
      [CreateUserFormFields.PhoneNumber]: '',
      [CreateUserFormFields.AvatarFileKey]: '',
    },
  });

  const { formState, reset, control, handleSubmit, watch } = formMethods;
  const firstName = watch(CreateUserFormFields.FirstName);
  const lastName = watch(CreateUserFormFields.LastName);
  const previewName = getFullName({ firstName, lastName }) || 'New User';

  const avatar = useAvatarSelection();

  const closeModalAndResetForm = useCallback(() => {
    reset();
    avatar.reset();
    closeModal();
  }, [reset, avatar, closeModal]);

  const onClose = useCallback(() => {
    // Block dialog dismissal while an upload or mutation is in flight.
    if (formState.isSubmitting) {
      return;
    }
    closeModalAndResetForm();
  }, [closeModalAndResetForm, formState.isSubmitting]);

  const { deleteAvatar } = useDeletePlatformUserAvatar();
  const { showErrorAlert } = useNotifications();

  const onFormSubmit = useCallback(
    async (formValues: CreateUserFormModel) => {
      const flush = await avatar.flush(modalProps?.partnerId);
      if (flush.status === AvatarFlushStatus.Failed) {
        if (flush.reason === AvatarFlushFailureReason.MissingPartner) {
          showErrorAlert('Cannot upload the photo: missing partner. Please reopen the form.');
        }
        return;
      }
      const avatarFileKey =
        flush.status === AvatarFlushStatus.Uploaded
          ? flush.fileKey
          : formValues[CreateUserFormFields.AvatarFileKey] || undefined;

      const preparedFormValues = {
        ...formValues,
        [CreateUserFormFields.FirstName]: formValues[CreateUserFormFields.FirstName].trim(),
        [CreateUserFormFields.LastName]: formValues[CreateUserFormFields.LastName].trim(),
        [CreateUserFormFields.PhoneNumber]: formValues[CreateUserFormFields.PhoneNumber]
          ? appendCodeToPhoneNumber(formValues[CreateUserFormFields.PhoneNumber])
          : undefined,
        [CreateUserFormFields.AvatarFileKey]: avatarFileKey,
      };
      try {
        await modalProps?.onSubmit(preparedFormValues);
        closeModalAndResetForm();
      } catch {
        if (flush.status === AvatarFlushStatus.Uploaded) {
          void deleteAvatar(
            getPlatformUserAvatarStorageKey(modalProps?.partnerId, flush.fileKey),
          );
        }
      }
    },
    [avatar, modalProps, closeModalAndResetForm, deleteAvatar, showErrorAlert],
  );

  return (
    <Dialog open={isOpened} onClose={onClose} fullWidth>
      <DialogTitle>
        <Typography>Create User</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack flexDirection="column">
          {modalProps?.partnerId !== null && modalProps?.partnerId !== undefined && (
            <AvatarUploader
              name={previewName}
              imageUrl={avatar.previewUrl}
              onFileChange={avatar.onFileChange}
            />
          )}

          <FormProvider {...formMethods}>
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              onSubmit={handleSubmit(onFormSubmit)}
            >
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={CreateUserFormFields.FirstName}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      sx={{
                        minWidth: '100%',
                      }}
                      placeholder="First Name"
                      error={!!formState.errors[CreateUserFormFields.FirstName]}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={CreateUserFormFields.LastName}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      sx={{
                        minWidth: '100%',
                      }}
                      placeholder="Last Name"
                      error={!!formState.errors[CreateUserFormFields.LastName]}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  control={control}
                  name={CreateUserFormFields.Email}
                  rules={{
                    required: true,
                    pattern: EMAIL_VALIDATION_REGEX,
                  }}
                  render={({ field }) => (
                    <TextField
                      placeholder="Email"
                      sx={{
                        minWidth: '100%',
                      }}
                      error={!!formState.errors[CreateUserFormFields.Email]}
                      {...field}
                    />
                  )}
                />
              </FormControl>

              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                  padding: '0.5rem 0',
                }}
              >
                <Controller
                  name={CreateUserFormFields.PhoneNumber}
                  rules={{
                    minLength: MIN_NUMBER_LENGTH,
                    maxLength: MAX_NUMBER_LENGTH,
                  }}
                  control={control}
                  render={({ field }) => (
                    <StyledLabel>
                      <Input
                        {...field}
                        error={!!formState.errors[CreateUserFormFields.PhoneNumber]}
                        placeholder={PHONE_FIELD_PLACEHOLDER}
                        type="tel"
                        inputComponent={TextMaskPhone}
                        disableUnderline
                        sx={{
                          minWidth: '100%',
                        }}
                        inputProps={{
                          sx: {
                            fontSize: '1rem',
                            paddingBlock: 2,
                          },
                        }}
                      />
                    </StyledLabel>
                  )}
                />
              </FormControl>

              <UserModalActions
                onCancel={onClose}
                isSubmitting={formState.isSubmitting}
                isFormValid={formState.isValid}
              />
            </form>
          </FormProvider>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
